import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { FaHome } from 'react-icons/fa';

const BreadcrumbContainer = styled.nav`
  background: var(--secondary-50);
  padding: var(--space-md) 0;
  margin-bottom: var(--space-lg);
  border-bottom: 1px solid var(--secondary-200);
`;

const BreadcrumbList = styled.ol`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--space-md);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  list-style: none;
  gap: var(--space-xs);
  font-size: 0.875rem;
`;

const BreadcrumbItem = styled.li`
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  
  &:not(:last-child)::after {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-right: 1px solid var(--secondary-400);
    border-bottom: 1px solid var(--secondary-400);
    transform: rotate(-45deg);
    margin: 0 var(--space-xs);
  }
`;

const BreadcrumbLink = styled(Link)`
  color: var(--secondary-600);
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  transition: color var(--transition-fast);
  
  &:hover {
    color: var(--primary-600);
  }
  
  &[aria-current="page"] {
    color: var(--secondary-900);
    font-weight: 500;
    pointer-events: none;
  }
`;

const BreadcrumbText = styled.span`
  color: var(--secondary-900);
  font-weight: 500;
`;

const Breadcrumbs = ({ items, showHome = true }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const baseUrl = 'https://scrumnx.com';
  
  // Generate breadcrumb items from location if not provided
  const generateBreadcrumbs = () => {
    const pathnames = location.pathname.split('/').filter((x) => x);
    const breadcrumbs = [];
    
    if (showHome) {
      breadcrumbs.push({
        label: t('common.home', 'Home'),
        path: '/',
        isCurrent: pathnames.length === 0
      });
    }
    
    pathnames.forEach((value, index) => {
      const path = `/${pathnames.slice(0, index + 1).join('/')}`;
      const isLast = index === pathnames.length - 1;
      
      // Translate common paths
      let label = value;
      if (value === 'services') label = t('common.services', 'Services');
      else if (value === 'blog') label = t('common.blog', 'Blog');
      else if (value === 'contact') label = t('common.contact', 'Contact');
      else if (value === 'privacy-policy') label = t('footer.privacyPolicy', 'Privacy Policy');
      else if (value === 'terms-of-service') label = t('footer.termsOfService', 'Terms of Service');
      else if (value === 'imprint') label = t('footer.imprint', 'Imprint');
      else {
        // Capitalize and replace hyphens
        label = value
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
      
      breadcrumbs.push({
        label,
        path,
        isCurrent: isLast
      });
    });
    
    return breadcrumbs;
  };
  
  const breadcrumbItems = items || generateBreadcrumbs();
  
  // Generate BreadcrumbList schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbItems.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.label,
      "item": `${baseUrl}${item.path}`
    }))
  };
  
  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      <BreadcrumbContainer aria-label={t('accessibility.breadcrumbNavigation', 'Breadcrumb navigation')}>
        <BreadcrumbList role="list">
          {breadcrumbItems.map((item, index) => (
            <BreadcrumbItem key={index} role="listitem">
              {item.isCurrent ? (
                <BreadcrumbText aria-current="page">
                  {index === 0 && showHome && <FaHome aria-hidden="true" />}
                  {item.label}
                </BreadcrumbText>
              ) : (
                <BreadcrumbLink to={item.path}>
                  {index === 0 && showHome && <FaHome aria-hidden="true" />}
                  {item.label}
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
          ))}
        </BreadcrumbList>
      </BreadcrumbContainer>
    </>
  );
};

export default Breadcrumbs;

